import api from './api';

/**
 * AI Service for frontend
 * Provides methods to call AI-powered features
 */
class AIService {
  /**
   * Detect project intent from natural language description
   * @param {string} description - User's natural language project description
   * @returns {Promise<Object>} Project type, confidence, reasoning
   */
  async detectIntent(description) {
    try {
      const response = await api.post('/ai/detect-intent', { description });
      return response.data;
    } catch (error) {
      console.error('Intent detection failed:', error);
      throw error;
    }
  }

  /**
   * Generate complete project descriptions
   * @param {Object} params - Parameters for description generation
   * @returns {Promise<Array<string>>} Array of 3 suggested descriptions
   */
  async completeDescription(params) {
    try {
      const response = await api.post('/ai/complete-description', params);
      return response.data;
    } catch (error) {
      console.error('Description completion failed:', error);
      throw error;
    }
  }

  /**
   * Compose one contractor-facing project brief from the client's rough note
   * plus the structured wizard answers. Facts-only; never mentions money.
   * @param {Object} params - { projectType, taskType, title, location,
   *   ownerNote, structuredBrief, photoCount, planCount }
   * @returns {Promise<Object>} raw response data ({ success, data: { brief } })
   */
  async composeBrief(params) {
    try {
      const response = await api.post('/ai/compose-brief', params);
      return response.data;
    } catch (error) {
      console.error('Brief composition failed:', error);
      throw error;
    }
  }

  /**
   * Analyze uploaded photo
   * @param {File} photoFile - Image file to analyze
   * @returns {Promise<Object>} Photo analysis results
   */
  async analyzePhoto(photoFile, context = {}) {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);
      ['projectType', 'taskType', 'description', 'location'].forEach((key) => {
        if (context[key]) {
          formData.append(key, context[key]);
        }
      });

      const response = await api.post('/ai/analyze-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      // Extract the actual analysis data from the response
      // API returns: { success: true, data: { detectedFeatures: [...], ... } }
      // Return just the data object for easier use
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Photo analysis failed:', error);
      throw error;
    }
  }

  /**
   * Estimate project budget
   * @param {Object} params - Project parameters
   * @returns {Promise<Object>} Budget estimation with breakdown
   */
  async estimateBudget(params) {
    try {
      const response = await api.post('/ai/estimate-budget', params);
      return response.data;
    } catch (error) {
      console.error('Budget estimation failed:', error);
      throw error;
    }
  }

  /**
   * Predict project timeline
   * @param {Object} params - Project parameters
   * @returns {Promise<Object>} Timeline prediction
   */
  async predictTimeline(params) {
    try {
      const response = await api.post('/ai/predict-timeline', params);
      return response.data;
    } catch (error) {
      console.error('Timeline prediction failed:', error);
      throw error;
    }
  }

  /**
   * Match contractors to project
   * @param {Object} params - Project parameters
   * @param {Array} contractors - Available contractors
   * @returns {Promise<Array>} Ranked contractors with match scores
   */
  async matchContractors(params, contractors) {
    try {
      const response = await api.post('/ai/match-contractors', {
        ...params,
        contractors
      });
      return response.data;
    } catch (error) {
      console.error('Contractor matching failed:', error);
      throw error;
    }
  }

  /**
   * Generate project milestones
   * @param {Object} params - Project parameters
   * @returns {Promise<Array>} Generated milestones
   */
  async generateMilestones(params) {
    try {
      const response = await api.post('/ai/generate-milestones', params);
      return response.data;
    } catch (error) {
      console.error('Milestone generation failed:', error);
      throw error;
    }
  }

  /**
   * Generate smart reply suggestions
   * @param {Object} params - Conversation context
   * @returns {Promise<Object>} Smart reply suggestions
   */
  async generateSmartReplies(params) {
    try {
      const response = await api.post('/ai/smart-replies', params);
      return response.data;
    } catch (error) {
      console.error('Smart reply generation failed:', error);
      // Return empty suggestions on error
      return { success: true, data: { suggestions: [] } };
    }
  }

  /**
   * Improve message text
   * @param {Object} params - Message to improve
   * @returns {Promise<Object>} Improved message
   */
  async improveMessage(params) {
    try {
      const response = await api.post('/ai/improve-message', params);
      return response.data;
    } catch (error) {
      console.error('Message improvement failed:', error);
      // Return original message on error
      return { success: true, data: { improved: params.message } };
    }
  }

  /**
   * Summarize conversation
   * @param {Object} params - Conversation ID
   * @returns {Promise<Object>} Conversation summary
   */
  async summarizeConversation(params) {
    try {
      const response = await api.post('/ai/summarize-conversation', params);
      return response.data;
    } catch (error) {
      console.error('Conversation summarization failed:', error);
      return { success: true, data: { summary: 'Unable to generate summary at this time.' } };
    }
  }

  /**
   * Process scope of work conversation
   * @param {Object} params - Conversation parameters
   * @returns {Promise<Object>} AI response with updated scope
   */
  async processScopeConversation(params) {
    try {
      const response = await api.post('/ai/scope-conversation', params);
      // Backend returns { success: true, data: {...} }, extract the data
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Scope conversation processing failed:', error);
      throw error;
    }
  }

  /**
   * Generate scope suggestions based on project
   * @param {Object} params - Project parameters
   * @returns {Promise<Object>} Scope suggestions
   */
  async generateScopeSuggestions(params) {
    try {
      const response = await api.post('/ai/scope-suggestions', params);
      // Backend returns { success: true, data: {...} }, extract the data
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Scope suggestion generation failed:', error);
      return { suggestions: [] };
    }
  }

  /**
   * Generate milestone description using AI
   * @param {Object} params - Milestone parameters
   * @returns {Promise<Object>} Generated description and suggestions
   */
  async generateMilestoneDescription(params) {
    try {
      const response = await api.post('/ai/milestone-description', params);
      // Backend returns { success: true, data: {...} }, extract the data
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Milestone description generation failed:', error);
      throw error;
    }
  }

  /**
   * Improve existing milestone description
   * @param {Object} params - Description to improve
   * @returns {Promise<Object>} Improved description
   */
  async improveMilestoneDescription(params) {
    try {
      const response = await api.post('/ai/improve-milestone-description', params);
      // Backend returns { success: true, data: {...} }, extract the data
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Milestone description improvement failed:', error);
      throw error;
    }
  }

  /**
   * Generate project update description using AI
   * @param {Object} params - { userPrompt, projectContext }
   * @returns {Promise<Object>} Generated description and suggestions
   */
  async generateProjectUpdateDescription(params) {
    try {
      const response = await api.post('/ai/project-update-description', params);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Project update description generation failed:', error);
      throw error;
    }
  }

  /**
   * Improve existing project update description
   * @param {Object} params - { description, projectContext }
   * @returns {Promise<Object>} Improved description
   */
  async improveProjectUpdateDescription(params) {
    try {
      const response = await api.post('/ai/improve-project-update-description', params);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Project update description improvement failed:', error);
      throw error;
    }
  }

  /**
   * Generate milestone dependencies using AI
   * @param {Object} params - Milestone parameters
   * @returns {Promise<Object>} Generated dependencies and suggestions
   */
  async generateMilestoneDependencies(params) {
    try {
      const response = await api.post('/ai/milestone-dependencies', params);
      // Backend returns { success: true, data: {...} }, extract the data
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Milestone dependencies generation failed:', error);
      throw error;
    }
  }

  /**
   * Improve existing milestone dependencies
   * @param {Object} params - Dependencies to improve
   * @returns {Promise<Object>} Improved dependencies
   */
  async improveMilestoneDependencies(params) {
    try {
      const response = await api.post('/ai/improve-milestone-dependencies', params);
      // Backend returns { success: true, data: {...} }, extract the data
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Milestone dependencies improvement failed:', error);
      throw error;
    }
  }

  /**
   * Generate milestone deliverables using AI
   * @param {Object} params - Milestone parameters
   * @returns {Promise<Object>} Generated deliverables and suggestions
   */
  async generateMilestoneDeliverables(params) {
    try {
      const response = await api.post('/ai/milestone-deliverables', params);
      // Backend returns { success: true, data: {...} }, extract the data
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Milestone deliverables generation failed:', error);
      throw error;
    }
  }

  /**
   * Improve existing milestone deliverables
   * @param {Object} params - Deliverables to improve
   * @returns {Promise<Object>} Improved deliverables
   */
  async improveMilestoneDeliverables(params) {
    try {
      const response = await api.post('/ai/improve-milestone-deliverables', params);
      // Backend returns { success: true, data: {...} }, extract the data
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Milestone deliverables improvement failed:', error);
      throw error;
    }
  }

  /**
   * Generate inspection report using AI
   * @param {Object} params - Inspection parameters
   * @returns {Promise<Object>} Generated report and suggestions
   */
  async generateInspectionReport(params) {
    try {
      const response = await api.post('/ai/generate-inspection-report', params);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Inspection report generation failed:', error);
      throw error;
    }
  }

  /**
   * Improve existing inspection report
   * @param {Object} params - Report to improve
   * @returns {Promise<Object>} Improved report
   */
  async improveInspectionReport(params) {
    try {
      const response = await api.post('/ai/improve-inspection-report', params);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Inspection report improvement failed:', error);
      throw error;
    }
  }

  /**
   * Analyze inspection photo using AI vision
   * @param {File} photoFile - Image file to analyze
   * @param {Object} context - Inspection context
   * @returns {Promise<Object>} Photo analysis results
   */
  async analyzeInspectionPhoto(photoFile, context = {}) {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);
      formData.append('context', JSON.stringify(context));

      const response = await api.post('/ai/analyze-inspection-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Inspection photo analysis failed:', error);
      throw error;
    }
  }

  /**
   * Transcribe voice notes for inspection
   * @param {Blob} audioBlob - Audio recording
   * @param {Object} context - Inspection context
   * @returns {Promise<Object>} Transcription results
   */
  async transcribeInspectionNotes(audioBlob, context = {}) {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'inspection-notes.webm');
      formData.append('context', JSON.stringify(context));

      const response = await api.post('/ai/transcribe-inspection-notes', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Inspection notes transcription failed:', error);
      throw error;
    }
  }

  /**
   * Check inspection report compliance
   * @param {Object} params - Report and requirements
   * @returns {Promise<Object>} Compliance check results
   */
  async checkInspectionCompliance(params) {
    try {
      const response = await api.post('/ai/check-inspection-compliance', params);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Inspection compliance check failed:', error);
      throw error;
    }
  }

  /**
   * Generate smart replies for verifier messages
   * @param {Object} params - Conversation context
   * @returns {Promise<Object>} Smart reply suggestions
   */
  async generateVerifierSmartReplies(params) {
    try {
      const response = await api.post('/ai/verifier-smart-replies', params);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Verifier smart reply generation failed:', error);
      return { success: true, data: { suggestions: [] } };
    }
  }

  /**
   * Match verifiers to project using AI
   * @param {Object} params - Project parameters
   * @returns {Promise<Array>} Ranked verifiers with match scores
   */
  async matchVerifiersToProject(params) {
    try {
      const response = await api.post('/ai/match-verifiers', params);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Verifier matching failed:', error);
      throw error;
    }
  }

  /**
   * Generate proposal suggestions using AI
   * @param {Object} context - Verification request context
   * @returns {Promise<Object>} Proposal suggestions
   */
  async generateProposalSuggestions(context) {
    try {
      const response = await api.post('/ai/proposal-suggestions', { context });
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      console.error('Proposal suggestions generation failed:', error);
      throw error;
    }
  }

  /**
   * Generate proposal text using AI
   * @param {Object} context - Verification request context
   * @returns {Promise<string>} Generated proposal text
   */
  async generateProposalText(context) {
    try {
      const response = await api.post('/ai/generate-proposal', { context });
      return response.data.success ? response.data.data.proposalText : response.data.proposalText;
    } catch (error) {
      console.error('Proposal text generation failed:', error);
      throw error;
    }
  }

  /**
   * Generate verification approach using AI
   * @param {Object} context - Verification request context
   * @returns {Promise<string>} Generated approach
   */
  async generateVerificationApproach(context) {
    try {
      const response = await api.post('/ai/generate-approach', { context });
      return response.data.success ? response.data.data.approach : response.data.approach;
    } catch (error) {
      console.error('Approach generation failed:', error);
      throw error;
    }
  }

  /**
   * Generate a professional bio for contractor profile
   * @param {Object} params - Bio generation parameters
   * @returns {Promise<Object>} Generated bio
   */
  async generateProfileBio(params) {
    try {
      const response = await api.post('/ai/generate-profile-bio', params, {
        timeout: 30000 // 30 second timeout
      });
      return response.data;
    } catch (error) {
      console.error('Profile bio generation failed:', error);
      throw error;
    }
  }
}

export default new AIService();
